import React, { useState, useEffect, useMemo } from "react";
import classNames from "classnames";
import Image from "react-bootstrap/Image";
import DetailsPanel from "./DetailsPanel";
import Button from "react-bootstrap/Button";
import StatusBadge from "../StatusBadge/StatusBadge";
import styles from "./DetailsPanel.module.scss";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import ImagePreviewLightbox from "../ImagePreviewLightbox";
import { singleUser } from "../../services/userService";
import { useAtomValue } from "jotai";
import { authAtom } from "../../states/auth";

const formatDateTimeToString = (dateObjTmp) => {
  const dateObj = new Date(dateObjTmp);
  if (dateObj) {
    const formattedDate = dateObj.toLocaleDateString("en-GB"); // Gives "dd/mm/yyyy"
    const formattedTime = dateObj.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // ensures AM/PM format
    });
    const formattedDateWithDashes = formattedDate?.replace(/\//g, "-"); // converts dd/mm/yyyy to dd-mm-yyyy
    return `${formattedDateWithDashes}, ${formattedTime}`;
  }
};

const getAddress = (userInfo) => {
  const addressArr = [];
  if (userInfo?.createdBy?.address) {
    addressArr.push(userInfo?.createdBy?.address);
  }
  if (userInfo?.createdBy?.city) {
    addressArr.push(userInfo?.createdBy?.city);
  }
  if (userInfo?.createdBy?.state?.name) {
    addressArr.push(userInfo?.createdBy?.state?.name);
  }
  if (userInfo?.createdBy?.country?.code) {
    addressArr.push(userInfo?.createdBy?.country?.code);
  }
  if (userInfo?.createdBy?.zipCode) {
    addressArr.push(userInfo?.createdBy?.zipCode);
  }
  return addressArr.join(", ");
};

const getPhone = (userInfo) => {
  let phoneNo = userInfo?.phoneNo || "";
  const countryCode = userInfo?.countryCode || "";
  console.log("asdasd", phoneNo);
  if (phoneNo.startsWith("0")) {
    phoneNo = phoneNo.slice(1);
  }

  const phoneString = `+${countryCode}${phoneNo}`;
  const parsed = parsePhoneNumberFromString(phoneString);

  if (!parsed) return "";

  // 📞 Example output: +91 91234 56789 or +1 (201) 555-0123
  return `+${parsed.countryCallingCode} ${parsed.formatNational().startsWith("0") ? parsed.formatNational().slice(1) : parsed.formatNational()}`;
};

const HistoryDetailsPanel = ({ onClose, item, setShowFilesView }) => {
  const [singleHistoryItem, setSingleHistoryItem] = useState(null);
  const [anotherMember, setAnotherMember] = useState(null);
  const [callDurations, setCallDurations] = useState({});
  const [totalSessionDuration, setTotalSessionDuration] = useState({});
  const [totalChatDuration, setTotalChatDuration] = useState({});
  const [loginUserId, setLoginUserId] = useState(null);
  const auth = useAtomValue(authAtom);

  function getDateTimeFormattedString(totalSeconds) {
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = String(Math.ceil(totalSeconds % 60)).padStart(2, "0");

    const parts = [];

    if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? "s" : ""}`);
    parts.push(`${seconds} sec${seconds !== 1 ? "s" : ""}`);

    return parts.join(", ");
  }

  function getDurationBetween(startTime, endTime) {
    if (!startTime || !endTime) {
      return {
        totalSeconds: 0,
        formatted: "00:00:00",
      };
    }
    const start = new Date(startTime);
    const end = new Date(endTime);

    const diffMs = Math.abs(end - start); // Always positive
    const totalSeconds = Math.floor(diffMs / 1000) || 0;

    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      totalSeconds: totalSeconds,
      formatted: getDateTimeFormattedString(totalSeconds),
    };
  }

  const getTotalConversationDuration = (calls = []) => {
    const allCallHistories = [];

    // Step 1: Flatten all callHistories from each call
    for (const call of calls) {
      if (Array.isArray(call.callHistories)) {
        for (const history of call.callHistories) {
          if (history.start_time && history.end_time) {
            const start = new Date(history.start_time).getTime();
            const end = new Date(history.end_time).getTime();

            allCallHistories.push({ start_time: start, end_time: end });
          }
        }
      }
    }

    // Step 2: Sort by start_time
    allCallHistories.sort((a, b) => a.start_time - b.start_time);

    // Step 3: Merge overlapping intervals
    const merged = [];
    for (const interval of allCallHistories) {
      if (
        !merged.length ||
        merged[merged.length - 1].end_time < interval.start_time
      ) {
        merged.push({ ...interval });
      } else {
        merged[merged.length - 1].end_time = Math.max(
          merged[merged.length - 1].end_time,
          interval.end_time,
        );
      }
    }

    // Step 4: Calculate total duration
    let totalSeconds = 0;
    for (const { start_time, end_time } of merged) {
      const duration = (end_time - start_time) / 1000;
      totalSeconds += Math.max(0, duration);
    }

    // Step 5: Format duration
    const formatDuration = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins} min${mins !== 1 ? "s" : ""} ${secs} sec${secs !== 1 ? "s" : ""}`;
    };

    return {
      totalSeconds: totalSeconds,
      formatted: formatDuration(totalSeconds),
    };
  };

  const identifyUser = (data, auth) => {
    let userMeta = { label: "", image: "", role: "" };
    if (data?.createdBy?.roleId == 3) {
      console.log("login user----------->", auth);
      if (data?.conversationType == "internal call") {
        userMeta = {
          label: "Expert",
          role: "expert",
          image: "/images/user-circle-yellow.svg",
        };
      } else if (data?.createdBy?.createdBy.id !== auth) {
        userMeta = {
          label: "Contact",
          role: "client",
          image: "/images/user-circle-gear-blue.svg",
        };
      } else {
        userMeta = {
          label: "External Expert",
          role: "external_expert",
          image: "/images/user-circle-dashed-red.svg",
        };
      }
    } else if (data?.createdBy?.roleId == 5) {
      userMeta = {
        label: "Contact",
        role: "client",
        image: "/images/user-circle-gear-blue.svg",
      };
    } else if (data?.createdBy?.roleId == 2) {
      if (data?.createdBy?.createdBy === data?.createdBy?.id) {
        userMeta = {
          label: "User",
          role: "internal_user",
          image: "/images/user-circle-yellow.svg",
        };
      } else {
        userMeta = {
          label: "User",
          role: "external_user",
          image: "/images/user-circle-yellow.svg",
        };
      }
    }
    return userMeta;
  };
  useEffect(() => {
    singleUser(auth?.user?.id).then((response) => {
      if (response?.data?.user) {
        const UserData = response?.data?.user;
        let loginUserId =
          UserData?.roleId === 2 ? UserData?.id : UserData?.createdBy?.id;
        setLoginUserId(loginUserId);
      }
    });
  }, [auth?.user]);

  useEffect(() => {
    console.log("SingleHistoryItem", singleHistoryItem);
    setSingleHistoryItem(item);
    setCallDurations(getTotalConversationDuration(item?.calls || []));
    setAnotherMember(
      item?.conversationMembers.find((e) => e.user_id != item.createdBy.id),
    );
    console.log(
      "anotherMmeber",
      item?.conversationMembers.find((e) => e.user_id != item.createdBy.id),
    );
    setTotalSessionDuration(() =>
      getDurationBetween(item?.endTime, item?.startTime || item?.createdAt),
    );
    setTotalChatDuration(() =>
      getDurationBetween(item?.firstMsgTimestamp, item?.endTime),
    );
  }, [item]);
  
  const userMeta = identifyUser(singleHistoryItem, loginUserId);
  const anotherCompanyMember = useMemo(() => {
    return singleHistoryItem?.conversationMembers?.find(
      (e) =>
        e.user?.companyName && e.user?.id != singleHistoryItem?.createdBy?.id,
    );
  }, [singleHistoryItem]);

  const displayData = useMemo(() => {
    if (!singleHistoryItem) return null;

    const isCreator =
      singleHistoryItem?.createdBy?.createdBy?.id === loginUserId;
    const isSupport = ["support call", "support chat"].includes(
      singleHistoryItem?.conversationType,
    );

    const targetUser =
      isCreator && isSupport && anotherCompanyMember?.user
        ? anotherCompanyMember.user
        : singleHistoryItem.createdBy;

    if (!targetUser) return {};

    // Build Address safely
    const addressParts = [
      targetUser.address,
      targetUser.city,
      targetUser.state?.name,
      targetUser.country?.code,
      targetUser.zipCode,
    ].filter(Boolean);

    const finalAddress =
      addressParts.length > 0
        ? addressParts.join(" ")
        : getAddress(singleHistoryItem) || "N/A";

    return {
      profilePic: targetUser.profilePic,
      name: `${targetUser.firstName || ""} ${targetUser.lastName || ""}`.trim(),
      email: targetUser.email,
      phone: getPhone(targetUser),
      companyName: targetUser.companyName || "N/A",
      address: finalAddress,
      // Fallback avatar URL if profilePic is missing
      avatarFallback: `https://ui-avatars.com/api/?name=${targetUser.firstName}+${targetUser.lastName}&background=007bff&color=fff&size=100`,
    };
  }, [singleHistoryItem, loginUserId, anotherCompanyMember]);

  if (!displayData) return null;
  return (
    <DetailsPanel isOpen={!!item}>
      <div
        className={classNames(
          styles["detail-panel-head"],
          "d-flex align-items-center justify-content-between gap-3",
        )}
      >
        <h2 className="h5 fw-semibold mb-0">Session Information</h2>
        <button type="button" className="btn-reset" onClick={onClose}>
          <Image src="/images/btn-close-square.svg" />
        </button>
      </div>
      <div className={styles["detail-panel-body"]}>
        <div className="d-flex align-items-center gap-3">
          {displayData.profilePic ? (
            <ImagePreviewLightbox
              className="user-avtar user-avtar-48"
              src={displayData.profilePic}
            >
              <Image
                width="48"
                height="48"
                alt="User Avatar"
                className="object-fit-cover rounded-circle"
                src={displayData.profilePic}
              />
            </ImagePreviewLightbox>
          ) : (
            <Image
              width="48"
              height="48"
              alt="User Avatar"
              className="object-fit-cover rounded-circle"
              src={displayData.avatarFallback}
            />
          )}
          <div>
            <h3 className="fs-20 fw-semibold mb-0">{displayData.name}</h3>
            <ul className="d-flex flex-wrap align-items-center gap-2 list-unstyled mb-0">
              <li className="fs-14">{displayData.email}</li>
              <li className="dot-separator md"></li>
              <li className="fs-14">{displayData.phone || "N/A"}</li>
            </ul>
          </div>
        </div>

        <div className="border-top pt-4 mt-4">
          <h4 className="text-blue fs-7 fw-medium mb-4">Company Details</h4>

          <div className="mb-3">
            <span className="fs-14 text-gray-500 d-block mb-2">
              Company Name
            </span>
            <div className="d-flex align-items-center gap-2">
              <Image
                width="32"
                height="32"
                alt="User Circle Gear Icon"
                src={userMeta.image}
              />
              {<span>{displayData.companyName}</span>}
            </div>
          </div>
          <div className="mb-3">
            <span className="fs-14 text-gray-500 d-block mb-2">
              Company Address
            </span>
            <p className="mb-0">{displayData.address}</p>
          </div>
        </div>

        <div className="border-top pt-4 mt-4">
          <h4 className="text-blue fs-7 fw-medium mb-4">Session Details</h4>

          <div className="mb-3">
            <span className="fs-14 text-gray-500 d-block mb-2">Channel</span>
            <ul className="d-flex flex-wrap align-content-center gap-2 list-unstyled mb-0">
              {singleHistoryItem?.conversationType?.includes("chat") ? (
                <li className="session-type-badge d-flex align-items-center gap-2">
                  <Image
                    src="/images/chats-circle.svg"
                    width={20}
                    height={20}
                    alt="Chat Circle Icon"
                  />{" "}
                  Chat
                </li>
              ) : (
                <li className="session-type-badge d-flex align-items-center gap-2">
                  <Image
                    src="/images/video-camera-blue.svg"
                    width={20}
                    height={20}
                    alt="Video Camera Blue Icon"
                  />{" "}
                  Video
                </li>
              )}
            </ul>
          </div>
          <div className="mb-3">
            <span className="fs-14 text-gray-500 d-block mb-2">
              {singleHistoryItem?.conversationType?.includes("chat")
                ? "Session Time"
                : "Session Timeline"}
            </span>
            <p className="mb-0">
              {formatDateTimeToString(
                singleHistoryItem?.startTime || singleHistoryItem?.createdAt,
              )}
              {singleHistoryItem?.endTime &&
                " - " + formatDateTimeToString(singleHistoryItem?.endTime)}
            </p>
          </div>
          {totalSessionDuration.totalSeconds > 0 && (
            <div className="mb-3">
              <span className="fs-14 text-gray-500 d-block mb-2">
                Total Session Duration
              </span>
              <p className="mb-0">{totalSessionDuration.formatted}</p>
            </div>
          )}
          {callDurations?.totalSeconds > 0 && (
            <div className="mb-3">
              <span className="fs-14 text-gray-500 d-block mb-2">
                Video Session Total Duration
              </span>
              <p className="mb-0">{callDurations?.formatted}</p>
            </div>
          )}
          <div className="mb-3">
            <span className="fs-14 text-gray-500 d-block mb-2">
              Chat Session Total Duration
            </span>
            {(totalChatDuration?.totalSeconds > 0 && (
              <p className="mb-0">{totalChatDuration?.formatted}</p>
            )) ||
              "0 mins"}
          </div>
        </div>

        {singleHistoryItem?.expertise != null && (
          <div className="border-top pt-4 mt-4">
            <h4 className="text-blue fs-7 fw-medium mb-4">Expert Details</h4>
            <div className="mb-3">
              <span className="fs-14 text-gray-500 d-block mb-2">Expert</span>
              <div className="d-flex align-items-center gap-2">
                {anotherMember?.user?.profilePic ? (
                  <ImagePreviewLightbox
                    className="user-avtar user-avtar-32"
                    src={anotherMember?.user?.profilePic}
                  >
                    <Image
                      width="32"
                      height="32"
                      alt="User Avatar"
                      className="object-fit-cover rounded-circle"
                      src={anotherMember?.user?.profilePic}
                    />
                  </ImagePreviewLightbox>
                ) : (
                  <Image
                    width={32}
                    height={32}
                    alt="User Avatar"
                    className="object-fit-cover rounded-circle"
                    src={`https://ui-avatars.com/api/?name=${anotherMember?.user?.firstName}+${anotherMember?.user?.lastName}&background=007bff&color=fff&size=100`}
                  />
                )}
                <span>
                  {anotherMember?.user?.firstName || ""}{" "}
                  {anotherMember?.user?.lastName || ""}
                </span>
              </div>
            </div>
            <div className="mb-3">
              <span className="fs-14 text-gray-500 d-block mb-2">
                Expertise
              </span>
              <p className="mb-0">{singleHistoryItem?.expertise?.name}</p>
            </div>
          </div>
        )}
      </div>

      <div className={styles["detail-panel-footer"]}>
        <Button
          // href="/history/all-files"
          onClick={() => setShowFilesView(true)}
          variant="primary"
          className="btn-md d-inline-flex align-items-center gap-1"
        >
          <Image
            src="/images/files-white.svg"
            width={20}
            height={20}
            alt="File White Icon"
          />{" "}
          Check Files
        </Button>
      </div>
    </DetailsPanel>
  );
};

export default HistoryDetailsPanel;
